import { ListCategoryIdType } from 'constants/list';
import { SUPPLIER_ID } from 'constants/supplier';
import { ListNameItemType } from 'hooks/search/useSearchContext';
import { caseInsensitiveLocaleCompare } from 'util/sort';

//
// --- Types ---

interface TransformSupplierListMembersToSearchResultOptions {
  excludePeople?: boolean;
}

/**
 * Converts the companyKeyToSupplierUsers and supplierCompanies list returned by
 * the API call to the format used by the SearchResults component.
 *
 * Sorts companies by ascending order of name.
 */
export function transformSupplierListMembersToSearchResult(
  supplierListMemberData: any,
  supplierVendorMappings: ReadonlyArray<any>,
  opts: TransformSupplierListMembersToSearchResultOptions = {}
): ReadonlyArray<any> {
  const { excludePeople = false } = opts;

  if (!supplierListMemberData || !Array.isArray(supplierListMemberData.supplierCompanies)) {
    return [];
  }

  const { companyKeyToSupplierUsers, supplierCompanies } = supplierListMemberData;
  return transformGroupedSupplierListToSearchResults(
    supplierCompanies,
    companyKeyToSupplierUsers,
    supplierVendorMappings,
    excludePeople
  );
}

function transformGroupedSupplierListToSearchResults(
  eipCompanies: ReadonlyArray<any>,
  companyKeyToSupplierUsers: any,
  supplierVendorMappings: ReadonlyArray<any>,
  excludePeople: boolean
) {
  if (!Array.isArray(eipCompanies) && !eipCompanies.length) {
    return [];
  }
  const supplierEipCompanyGroups: ReadonlyArray<any> =
    Array.isArray(supplierVendorMappings) &&
    supplierVendorMappings.length &&
    Array.isArray(eipCompanies) &&
    eipCompanies.length
      ? getSupplierEipCompanyGroups(supplierVendorMappings, eipCompanies)
      : [];

  // use grouped data to create final search result nodes to use in TreeDataProvider's
  const listSearchResultNodes = supplierEipCompanyGroups
    .reduce((allSearchResultNodes, currentSupplierGroup) => {
      const { supplierId, supplierName, eipCompanies } = currentSupplierGroup;
      const eipCompanyCount = eipCompanies.length;

      // special case for unfamilied vendors. the supplier vendor mappings uses a fake S_0 supplier to group these
      if (supplierId === SUPPLIER_ID.UNFAMILIED) {
        return [
          ...allSearchResultNodes,
          ...eipCompanies.map((eipCompany: any) => {
            return buildSearchResultElementFromEipCompany(
              eipCompany,
              excludePeople,
              companyKeyToSupplierUsers[eipCompany.companyKey],
              supplierId
            );
          })
        ];
      }

      // add supplier node with eip company children nodes if multiple eip companies exist for a single supplier
      if (eipCompanyCount > 1) {
        const sortedEipCompanies = eipCompanies.sort((company1: any, company2: any) =>
          caseInsensitiveLocaleCompare(company1.companyName, company2.companyName)
        );
        return [
          ...allSearchResultNodes,
          {
            children: sortedEipCompanies.map((eipCompany: any) => {
              return buildSearchResultElementFromEipCompany(
                eipCompany,
                excludePeople,
                companyKeyToSupplierUsers[eipCompany.companyKey],
                supplierId
              );
            }),
            id: supplierId,
            isSupplier: true,
            leaf: false,
            name: supplierName,
            uiNodeId: `list-supplier-${supplierId}`
          }
        ];
      }

      // return a single level node with eip company information if only one EIP company found for the supplier
      if (eipCompanyCount === 1) {
        const eipCompany = eipCompanies[0];
        return [
          ...allSearchResultNodes,
          buildSearchResultElementFromEipCompany(
            eipCompany,
            excludePeople,
            companyKeyToSupplierUsers[eipCompany.companyKey],
            supplierId
          )
        ];
      }

      // no eip companies, exclude this supplier and just return all nodes without modifying final collection
      return allSearchResultNodes;
    }, [])
    .sort((resultNode1: any, resultNode2: any) => caseInsensitiveLocaleCompare(resultNode1.name, resultNode2.name));

  return listSearchResultNodes;
}

function buildSearchResultElementFromEipCompany(
  eipCompany: { companyName: string; companyKey: number },
  excludePeople: boolean,
  usersForCompany: ReadonlyArray<any>,
  supplierId?: string
) {
  const hasPeople = !!usersForCompany && usersForCompany.length > 0;
  const { companyName, companyKey } = eipCompany;

  return {
    children:
      !excludePeople && hasPeople ? transformSupplierListMemberPersonToSearchResult(companyName, usersForCompany) : [],
    eipCompanyKey: companyKey,
    hasPersons: !excludePeople && hasPeople,
    id: supplierId,
    isSupplier: true,
    leaf: excludePeople || !hasPeople,
    name: companyName,
    parentId: supplierId,
    uiNodeId: `list-supplier-${companyKey}`
  };
}

/**
 * Creates a data structure for suppliers, placing all existing and matching eip companies in an array under supplier data
 */
function getSupplierEipCompanyGroups(
  supplierVendorMappings: ReadonlyArray<{ supplier: any; vendors: any }>,
  eipCompanies: ReadonlyArray<any>
): any {
  // create a map of EIP company keys to eip company objects for quick lookup
  const eipCompanyMap = new Map<number, any>(
    eipCompanies.map((eipCompany: any) => [eipCompany.companyKey, eipCompany])
  );

  return supplierVendorMappings.map(supplierVendorMapping => {
    const { supplier, vendors } = supplierVendorMapping;

    return {
      supplierId: supplier.id,
      supplierName: supplier.name,
      eipCompanies: Array.isArray(vendors)
        ? vendors.reduce((allVendorEipCompanies: ReadonlyArray<any>, currentVendor: any) => {
            const vendorCompanyKey = parseInt(currentVendor.eip_companykey, 10);
            return eipCompanyMap.has(vendorCompanyKey)
              ? [...allVendorEipCompanies, eipCompanyMap.get(vendorCompanyKey)]
              : allVendorEipCompanies;
          }, [])
        : []
    };
  });
}

/**
 * Grouped supplier lists are created during `transformSupplierListMembersToSearchResult` when
 * supplierVendorMappings are provided. This will potentially create nested nodes, at most one
 * level deep. Use this function to get a list of all company keys from a grouped list.
 *
 * This will also work for a flat list (or a grouped list that does not have any combined suppliers)
 */
export function getCompanyKeysFromGroupedSupplierList(groupedSupplierList: ReadonlyArray<any>): ReadonlyArray<number> {
  if (!Array.isArray(groupedSupplierList) || !groupedSupplierList.length) {
    return [];
  }
  const companyKeys = groupedSupplierList.reduce((allCompanyKeys, currentTopLevelNode) => {
    if (Array.isArray(currentTopLevelNode.children) && currentTopLevelNode.children.length) {
      return [
        ...allCompanyKeys,
        parseInt(currentTopLevelNode.eipCompanyKey, 10),
        ...currentTopLevelNode.children.map((childNode: any) => parseInt(childNode.eipCompanyKey, 10))
      ];
    }

    return currentTopLevelNode.eipCompanyKey
      ? [...allCompanyKeys, parseInt(currentTopLevelNode.eipCompanyKey, 10)]
      : allCompanyKeys;
  }, []);

  // filter out any undefined or NaN values
  return companyKeys.filter((companyKey: number | typeof NaN) => companyKey);
}

/**
 * Converts a given list of users associated with a given company name to the
 * format used by the SearchResults component.
 *
 * Sorts users by ascending order of last name, then first name.
 */
export function transformSupplierListMemberPersonToSearchResult(companyName: string, children: any) {
  if (!Array.isArray(children) || !children.length) {
    return [];
  }

  const sortedChildren = children.sort(
    (child1, child2) =>
      caseInsensitiveLocaleCompare(child1.lastName, child2.lastName) ||
      caseInsensitiveLocaleCompare(child1.firstName, child2.firstName)
  );

  return sortedChildren.map(
    ({
      businessTitle,
      cellPhone,
      companyKey,
      emailAddress,
      firstName,
      lastLoginSuccess,
      lastName,
      personKey,
      personStatusCode,
      username,
      workPhone
    }) => ({
      businessTitle,
      cellPhone,
      companyKey,
      emailAddress,
      isPerson: true,
      lastLoginSuccessTimestamp: lastLoginSuccess,
      leaf: true,
      name: `${firstName} ${lastName}`,
      personKey,
      personStatusCode,
      uiNodeId: `list-person-${personKey}`,
      vendorName: companyName,
      username,
      workPhone
    })
  );
}

/**
 * convert global supplier list result to ListNameItem type used in useSearchContext state for
 * providing available list names in the filter panel
 */
export function transformSupplierListResultsToListNameItems(
  globalSupplierLists: ReadonlyArray<any>,
  listCategoryId: ListCategoryIdType
): ReadonlyArray<ListNameItemType> {
  if (!Array.isArray(globalSupplierLists) || !globalSupplierLists.length) {
    return [];
  }

  return globalSupplierLists.map((globalSupplierList: any) => ({
    key: globalSupplierList.listKey.toString(),
    listCategoryId,
    name: globalSupplierList.listName
  }));
}
